import React, { useState, useEffect } from 'react';
import {
  Plus, Edit2, Trash2, X, Tag, Folder, FileText, Save, Settings
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface PostType {
  id: number;
  client_id: number;
  name: string;
  category: string;
  description?: string;
  slug?: string;
  default_template?: string;
  is_active: boolean;
  usage_count?: number;
}

interface Taxonomy {
  id: number;
  post_type_id: number;
  client_id: number;
  name: string;
  slug: string;
  description?: string;
}

interface Term {
  id: number;
  taxonomy_id: number;
  name: string;
  slug: string;
  description?: string;
  sort_order?: number;
}

interface Post {
  id: number;
  post_type_id: number;
  client_id: number;
  title: string;
  slug: string;
  content?: string;
  meta_title?: string;
  meta_description?: string;
  custom_fields?: string;
  status: 'draft' | 'published' | 'archived';
  published_at?: string;
}

interface PostTypesTabProps {
  clientId: number;
}

export default function PostTypesTab({ clientId }: PostTypesTabProps) {
  const [mainTab, setMainTab] = useState<'post_types' | 'taxonomies'>('post_types');
  const [subTab, setSubTab] = useState<'posts' | 'fields' | 'settings'>('posts');
  const [postTypes, setPostTypes] = useState<PostType[]>([]);
  const [taxonomies, setTaxonomies] = useState<Taxonomy[]>([]);
  const [selectedPostType, setSelectedPostType] = useState<PostType | null>(null);
  const [selectedTaxonomy, setSelectedTaxonomy] = useState<Taxonomy | null>(null);
  const [terms, setTerms] = useState<Term[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);

  const [showCreatePostTypeModal, setShowCreatePostTypeModal] = useState(false);
  const [showCreateTaxonomyModal, setShowCreateTaxonomyModal] = useState(false);
  const [showCreateTermModal, setShowCreateTermModal] = useState(false);
  const [showCreatePostModal, setShowCreatePostModal] = useState(false);

  const [postTypeForm, setPostTypeForm] = useState({ name: '', slug: '', description: '' });
  const [taxonomyForm, setTaxonomyForm] = useState({ name: '', slug: '', description: '', post_type_id: 0 });
  const [termForm, setTermForm] = useState({ name: '', slug: '', description: '' });
  const [postForm, setPostForm] = useState({
    title: '',
    slug: '',
    content: '',
    meta_title: '',
    meta_description: '',
    status: 'draft' as 'draft' | 'published' | 'archived',
    custom_field_values: {} as Record<string, any>
  });

  // Field configurations
  const [postTypeFields, setPostTypeFields] = useState({
    title: true,
    slug: true,
    content: true,
    meta_title: true,
    meta_description: true
  });

  const [taxonomyFields, setTaxonomyFields] = useState({
    title: true,
    slug: true,
    content: false,
    meta_title: true,
    meta_description: true
  });

  const [customFields, setCustomFields] = useState<Array<{ name: string; type: string; description: string }>>([]);
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [newFieldForm, setNewFieldForm] = useState({ name: '', type: 'text', description: '' });

  useEffect(() => {
    loadData();
  }, [clientId]);

  useEffect(() => {
    if (selectedTaxonomy) {
      loadTerms(selectedTaxonomy.id);
    }
  }, [selectedTaxonomy]);

  useEffect(() => {
    if (selectedPostType) {
      loadPosts(selectedPostType.id);
    }
  }, [selectedPostType]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadPostTypes(), loadTaxonomies()]);
    } finally {
      setLoading(false);
    }
  };

  const loadPostTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/post-types`, {
        params: { client_id: clientId }
      });
      setPostTypes(response.data);
    } catch (error) {
      console.error('Failed to load post types:', error);
    }
  };

  const loadTaxonomies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taxonomies`, {
        params: { client_id: clientId }
      });
      setTaxonomies(response.data);
    } catch (error) {
      console.error('Failed to load taxonomies:', error);
    }
  };

  const loadTerms = async (taxonomyId: number) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taxonomies/${taxonomyId}/terms`);
      setTerms(response.data);
    } catch (error) {
      console.error('Failed to load terms:', error);
      setTerms([]);
    }
  };

  const loadPosts = async (postTypeId: number) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/posts?post_type_id=${postTypeId}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to load posts:', error);
      setPosts([]);
    }
  };

  const handleCreatePostType = async () => {
    try {
      if (!postTypeForm.name.trim()) {
        alert('Please enter a post type name');
        return;
      }

      await axios.post(`${API_BASE_URL}/post-types`, {
        name: postTypeForm.name,
        description: postTypeForm.description,
        category: 'custom',
        client_id: clientId,
        is_active: true
      });

      setShowCreatePostTypeModal(false);
      setPostTypeForm({ name: '', slug: '', description: '' });
      await loadPostTypes();
    } catch (error: any) {
      alert(`Failed to create post type: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleUpdatePostType = async () => {
    if (!selectedPostType) return;
    try {
      await axios.put(`${API_BASE_URL}/post-types/${selectedPostType.id}`, {
        name: postTypeForm.name,
        description: postTypeForm.description,
        category: 'custom'
      });
      await loadPostTypes();
      setSelectedPostType({ ...selectedPostType, name: postTypeForm.name, description: postTypeForm.description });
    } catch (error: any) {
      alert(`Failed to update post type: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCreateTaxonomy = async () => {
    try {
      if (!taxonomyForm.name.trim()) {
        alert('Please enter a taxonomy name');
        return;
      }

      if (!taxonomyForm.post_type_id) {
        alert('Please select a post type');
        return;
      }

      await axios.post(`${API_BASE_URL}/taxonomies`, {
        post_type_id: taxonomyForm.post_type_id,
        client_id: clientId,
        name: taxonomyForm.name,
        slug: taxonomyForm.slug,
        description: taxonomyForm.description
      });

      setShowCreateTaxonomyModal(false);
      setTaxonomyForm({ name: '', slug: '', description: '', post_type_id: 0 });
      await loadTaxonomies();
    } catch (error: any) {
      alert(`Failed to create taxonomy: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleUpdateTaxonomy = async () => {
    if (!selectedTaxonomy) return;
    try {
      await axios.put(`${API_BASE_URL}/taxonomies/${selectedTaxonomy.id}`, {
        name: taxonomyForm.name,
        slug: taxonomyForm.slug,
        description: taxonomyForm.description
      });
      await loadTaxonomies();
      setSelectedTaxonomy({ ...selectedTaxonomy, name: taxonomyForm.name, slug: taxonomyForm.slug, description: taxonomyForm.description });
    } catch (error: any) {
      alert(`Failed to update taxonomy: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCreateTerm = async () => {
    if (!selectedTaxonomy) return;
    try {
      if (!termForm.name.trim()) {
        alert('Please enter a term name');
        return;
      }

      await axios.post(`${API_BASE_URL}/taxonomies/${selectedTaxonomy.id}/terms`, {
        name: termForm.name,
        slug: termForm.slug,
        description: termForm.description
      });

      setShowCreateTermModal(false);
      setTermForm({ name: '', slug: '', description: '' });
      await loadTerms(selectedTaxonomy.id);
    } catch (error: any) {
      alert(`Failed to create term: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeletePostType = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/post-types/${id}`);
      if (selectedPostType?.id === id) setSelectedPostType(null);
      await loadData();
    } catch (error: any) {
      alert(`Failed to delete: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteTaxonomy = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/taxonomies/${id}`);
      if (selectedTaxonomy?.id === id) setSelectedTaxonomy(null);
      await loadData();
    } catch (error: any) {
      alert(`Failed to delete: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeleteTerm = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      if (!selectedTaxonomy) return;
      await axios.delete(`${API_BASE_URL}/taxonomies/${selectedTaxonomy.id}/terms/${id}`);
      await loadTerms(selectedTaxonomy.id);
    } catch (error: any) {
      alert(`Failed to delete: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCreatePost = async () => {
    if (!selectedPostType) return;
    try {
      if (!postForm.title.trim()) {
        alert('Please enter a title');
        return;
      }

      // Auto-generate slug from title if not provided
      const slug = postForm.slug || postForm.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

      await axios.post(`${API_BASE_URL}/posts`, {
        post_type_id: selectedPostType.id,
        client_id: clientId,
        title: postForm.title,
        slug: slug,
        content: postForm.content,
        meta_title: postForm.meta_title,
        meta_description: postForm.meta_description,
        status: postForm.status,
        custom_fields: postForm.custom_field_values
      });

      setShowCreatePostModal(false);
      setPostForm({
        title: '',
        slug: '',
        content: '',
        meta_title: '',
        meta_description: '',
        status: 'draft',
        custom_field_values: {}
      });
      await loadPosts(selectedPostType.id);
    } catch (error: any) {
      alert(`Failed to create post: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleDeletePost = async (id: number, title: string) => {
    if (!confirm(`Delete "${title}"?`)) return;
    try {
      if (!selectedPostType) return;
      await axios.delete(`${API_BASE_URL}/posts/${id}`);
      await loadPosts(selectedPostType.id);
    } catch (error: any) {
      alert(`Failed to delete: ${error.response?.data?.error || error.message}`);
    }
  };

  const getPostTypeName = (postTypeId: number) => {
    return postTypes.find(pt => pt.id === postTypeId)?.name || 'Unknown';
  };

  const handleSelectItem = (item: PostType | Taxonomy, type: 'post_type' | 'taxonomy') => {
    if (type === 'post_type') {
      setSelectedPostType(item as PostType);
      setPostTypeForm({
        name: (item as PostType).name,
        slug: (item as PostType).slug || '',
        description: (item as PostType).description || ''
      });
    } else {
      setSelectedTaxonomy(item as Taxonomy);
      setTaxonomyForm({
        name: (item as Taxonomy).name,
        slug: (item as Taxonomy).slug,
        description: (item as Taxonomy).description || '',
        post_type_id: (item as Taxonomy).post_type_id
      });
    }
    setSubTab('posts');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  const currentFields = mainTab === 'post_types' ? postTypeFields : taxonomyFields;
  const setCurrentFields = mainTab === 'post_types' ? setPostTypeFields : setTaxonomyFields;

  return (
    <div className="h-[calc(100vh-300px)]">
      {/* Main Top Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex">
          <button
            onClick={() => {
              setMainTab('post_types');
              setSelectedTaxonomy(null);
            }}
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              mainTab === 'post_types'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Post Types
          </button>
          <button
            onClick={() => {
              setMainTab('taxonomies');
              setSelectedPostType(null);
            }}
            className={`px-6 py-3 text-sm font-medium border-b-2 ${
              mainTab === 'taxonomies'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Taxonomies
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100%-49px)]">
        {/* Left Sidebar - List */}
        <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col overflow-hidden">
          <div className="p-3 border-b border-gray-200">
            <button
              onClick={() => mainTab === 'post_types' ? setShowCreatePostTypeModal(true) : setShowCreateTaxonomyModal(true)}
              className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New {mainTab === 'post_types' ? 'Post Type' : 'Taxonomy'}
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            {mainTab === 'post_types' ? (
              postTypes.length > 0 ? (
                postTypes.map(pt => (
                  <div key={pt.id} className="relative group mb-1">
                    <button
                      onClick={() => handleSelectItem(pt, 'post_type')}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm ${
                        selectedPostType?.id === pt.id
                          ? 'bg-blue-100 text-blue-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Tag className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{pt.name}</span>
                      </div>
                    </button>
                    <div className="absolute right-2 top-2 hidden group-hover:flex gap-1 bg-white rounded shadow-sm border">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePostType(pt.id, pt.name);
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-xs text-gray-500">No post types yet</div>
              )
            ) : (
              taxonomies.length > 0 ? (
                taxonomies.map(tax => (
                  <div key={tax.id} className="relative group mb-1">
                    <button
                      onClick={() => handleSelectItem(tax, 'taxonomy')}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded text-sm ${
                        selectedTaxonomy?.id === tax.id
                          ? 'bg-green-100 text-green-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Folder className="w-4 h-4 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="truncate">{tax.name}</div>
                          <div className="text-xs text-gray-500 truncate">{getPostTypeName(tax.post_type_id)}</div>
                        </div>
                      </div>
                    </button>
                    <div className="absolute right-2 top-2 hidden group-hover:flex gap-1 bg-white rounded shadow-sm border">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteTaxonomy(tax.id, tax.name);
                        }}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-xs text-gray-500">No taxonomies yet</div>
              )
            )}
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white">
          {(mainTab === 'post_types' && selectedPostType) || (mainTab === 'taxonomies' && selectedTaxonomy) ? (
            <>
              {/* Sub Tabs */}
              <div className="border-b border-gray-200">
                <div className="flex px-4">
                  <button
                    onClick={() => setSubTab('posts')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 ${
                      subTab === 'posts'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {mainTab === 'taxonomies' ? 'Terms' : 'Posts'}
                  </button>
                  <button
                    onClick={() => setSubTab('fields')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 ${
                      subTab === 'fields'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Fields
                  </button>
                  <button
                    onClick={() => setSubTab('settings')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 ${
                      subTab === 'settings'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Settings
                  </button>
                </div>
              </div>

              {/* Sub Tab Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {subTab === 'posts' && (
                  <>
                    {mainTab === 'taxonomies' && selectedTaxonomy ? (
                      // Terms List
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-semibold text-gray-900">Terms</h4>
                          <button
                            onClick={() => setShowCreateTermModal(true)}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Add Term
                          </button>
                        </div>

                        {terms.length > 0 ? (
                          <div className="space-y-2">
                            {terms.map(term => (
                              <div key={term.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-gray-900 truncate">{term.name}</div>
                                    <div className="text-xs text-gray-500 font-mono">{term.slug}</div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeleteTerm(term.id, term.name)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500 mb-2">No terms yet</p>
                            <button
                              onClick={() => setShowCreateTermModal(true)}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              Add first term
                            </button>
                          </div>
                        )}
                      </>
                    ) : mainTab === 'post_types' && selectedPostType ? (
                      // Post Type Posts List
                      <>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-md font-semibold text-gray-900">Posts</h4>
                          <button
                            onClick={() => setShowCreatePostModal(true)}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Add Post
                          </button>
                        </div>

                        {posts.length > 0 ? (
                          <div className="space-y-2">
                            {posts.map(post => (
                              <div key={post.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                  <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-gray-900 truncate">{post.title}</div>
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                      <span className="font-mono">{post.slug}</span>
                                      <span className={`px-1.5 py-0.5 rounded ${
                                        post.status === 'published' ? 'bg-green-100 text-green-700' :
                                        post.status === 'archived' ? 'bg-gray-100 text-gray-700' :
                                        'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {post.status}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeletePost(post.id, post.title)}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-12">
                            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                            <p className="text-gray-500 mb-2">No posts yet</p>
                            <button
                              onClick={() => setShowCreatePostModal(true)}
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              Add first post
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">Select a {mainTab === 'post_types' ? 'post type' : 'taxonomy'} to view {mainTab === 'post_types' ? 'posts' : 'terms'}</p>
                      </div>
                    )}
                  </>
                )}

                {subTab === 'fields' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h4 className="text-md font-semibold text-gray-900">Field Configuration</h4>
                        <p className="text-sm text-gray-600 mt-1">
                          Select which fields should be available when creating {mainTab === 'taxonomies' ? 'terms' : 'posts'} of this type.
                        </p>
                      </div>
                      <button
                        onClick={() => setShowAddFieldModal(true)}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Custom Field
                      </button>
                    </div>

                    {/* Standard Fields */}
                    <div className="mb-6">
                      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Standard Fields</h5>
                      <div className="space-y-2">
                        {Object.entries(currentFields).map(([key, value]) => (
                          <label key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => setCurrentFields({ ...currentFields, [key]: e.target.checked })}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-900 capitalize">
                                {key.replace('_', ' ')}
                              </div>
                              <div className="text-xs text-gray-500">
                                {key === 'title' && 'The main title/name field'}
                                {key === 'slug' && 'URL-friendly identifier'}
                                {key === 'content' && 'Main content/description area'}
                                {key === 'meta_title' && 'SEO title tag'}
                                {key === 'meta_description' && 'SEO meta description'}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Custom Fields */}
                    {customFields.length > 0 && (
                      <div className="mb-6">
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Custom Fields</h5>
                        <div className="space-y-2">
                          {customFields.map((field, index) => (
                            <div key={index} className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900">
                                  {field.name}
                                  <span className="ml-2 text-xs text-gray-500 font-normal">({field.type})</span>
                                </div>
                                {field.description && (
                                  <div className="text-xs text-gray-500">{field.description}</div>
                                )}
                              </div>
                              <button
                                onClick={() => setCustomFields(customFields.filter((_, i) => i !== index))}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="pt-4 border-t">
                      <button className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 flex items-center gap-2">
                        <Save className="w-4 h-4" />
                        Save Field Configuration
                      </button>
                    </div>
                  </div>
                )}

                {subTab === 'settings' && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-4">
                      {mainTab === 'post_types' ? 'Post Type' : 'Taxonomy'} Settings
                    </h4>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                        <input
                          type="text"
                          value={mainTab === 'post_types' ? postTypeForm.name : taxonomyForm.name}
                          onChange={(e) => mainTab === 'post_types'
                            ? setPostTypeForm({...postTypeForm, name: e.target.value})
                            : setTaxonomyForm({...taxonomyForm, name: e.target.value})
                          }
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
                        <input
                          type="text"
                          value={mainTab === 'post_types' ? postTypeForm.slug : taxonomyForm.slug}
                          onChange={(e) => mainTab === 'post_types'
                            ? setPostTypeForm({...postTypeForm, slug: e.target.value})
                            : setTaxonomyForm({...taxonomyForm, slug: e.target.value})
                          }
                          placeholder="Auto-generated"
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                        <textarea
                          value={mainTab === 'post_types' ? postTypeForm.description : taxonomyForm.description}
                          onChange={(e) => mainTab === 'post_types'
                            ? setPostTypeForm({...postTypeForm, description: e.target.value})
                            : setTaxonomyForm({...taxonomyForm, description: e.target.value})
                          }
                          rows={3}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div className="pt-4">
                        <button
                          onClick={mainTab === 'post_types' ? handleUpdatePostType : handleUpdateTaxonomy}
                          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          Save Changes
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                {mainTab === 'post_types' ? (
                  <>
                    <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Select a post type</p>
                  </>
                ) : (
                  <>
                    <Folder className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Select a taxonomy</p>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals - Same as before */}
      {showCreatePostTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create Post Type</h3>
              <button onClick={() => setShowCreatePostTypeModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={postTypeForm.name}
                  onChange={(e) => setPostTypeForm({...postTypeForm, name: e.target.value})}
                  placeholder="e.g., Team Members, Services"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowCreatePostTypeModal(false)} className="px-4 py-2 text-sm border rounded-lg">
                Cancel
              </button>
              <button onClick={handleCreatePostType} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateTaxonomyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create Taxonomy</h3>
              <button onClick={() => setShowCreateTaxonomyModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Post Type *</label>
                <select
                  value={taxonomyForm.post_type_id}
                  onChange={(e) => setTaxonomyForm({...taxonomyForm, post_type_id: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value={0}>Select post type...</option>
                  {postTypes.map(pt => (
                    <option key={pt.id} value={pt.id}>{pt.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={taxonomyForm.name}
                  onChange={(e) => setTaxonomyForm({...taxonomyForm, name: e.target.value})}
                  placeholder="e.g., Roles, Service Types"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowCreateTaxonomyModal(false)} className="px-4 py-2 text-sm border rounded-lg">
                Cancel
              </button>
              <button onClick={handleCreateTaxonomy} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateTermModal && selectedTaxonomy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Term</h3>
              <button onClick={() => setShowCreateTermModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={termForm.name}
                  onChange={(e) => setTermForm({...termForm, name: e.target.value})}
                  placeholder="e.g., Developer, Web Design"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
                <input
                  type="text"
                  value={termForm.slug}
                  onChange={(e) => setTermForm({...termForm, slug: e.target.value})}
                  placeholder="Auto-generated"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={termForm.description}
                  onChange={(e) => setTermForm({...termForm, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowCreateTermModal(false)} className="px-4 py-2 text-sm border rounded-lg">
                Cancel
              </button>
              <button onClick={handleCreateTerm} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                Add
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Post Modal */}
      {showCreatePostModal && selectedPostType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Create Post - {selectedPostType.name}</h3>
              <button onClick={() => setShowCreatePostModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={postForm.title}
                  onChange={(e) => setPostForm({...postForm, title: e.target.value})}
                  placeholder="Enter post title"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
                <input
                  type="text"
                  value={postForm.slug}
                  onChange={(e) => setPostForm({...postForm, slug: e.target.value})}
                  placeholder="auto-generated-from-title"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to auto-generate from title</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                <textarea
                  value={postForm.content}
                  onChange={(e) => setPostForm({...postForm, content: e.target.value})}
                  placeholder="Main content..."
                  rows={6}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Title</label>
                <input
                  type="text"
                  value={postForm.meta_title}
                  onChange={(e) => setPostForm({...postForm, meta_title: e.target.value})}
                  placeholder="SEO title"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meta Description</label>
                <textarea
                  value={postForm.meta_description}
                  onChange={(e) => setPostForm({...postForm, meta_description: e.target.value})}
                  placeholder="SEO description"
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={postForm.status}
                  onChange={(e) => setPostForm({...postForm, status: e.target.value as 'draft' | 'published' | 'archived'})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Custom Fields */}
              {customFields.length > 0 && (
                <>
                  <div className="col-span-2 mt-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Custom Fields</h4>
                  </div>
                  {customFields.map((field, index) => (
                    <div key={index}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {field.name}
                        {field.description && <span className="text-xs text-gray-500 ml-2">({field.description})</span>}
                      </label>
                      {field.type === 'textarea' || field.type === 'wysiwyg' ? (
                        <textarea
                          value={postForm.custom_field_values[field.name] || ''}
                          onChange={(e) => setPostForm({
                            ...postForm,
                            custom_field_values: { ...postForm.custom_field_values, [field.name]: e.target.value }
                          })}
                          rows={field.type === 'wysiwyg' ? 6 : 3}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      ) : field.type === 'checkbox' ? (
                        <input
                          type="checkbox"
                          checked={postForm.custom_field_values[field.name] || false}
                          onChange={(e) => setPostForm({
                            ...postForm,
                            custom_field_values: { ...postForm.custom_field_values, [field.name]: e.target.checked }
                          })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                      ) : field.type === 'select' ? (
                        <select
                          value={postForm.custom_field_values[field.name] || ''}
                          onChange={(e) => setPostForm({
                            ...postForm,
                            custom_field_values: { ...postForm.custom_field_values, [field.name]: e.target.value }
                          })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select...</option>
                        </select>
                      ) : (
                        <input
                          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : field.type === 'date' ? 'date' : field.type === 'datetime' ? 'datetime-local' : 'text'}
                          value={postForm.custom_field_values[field.name] || ''}
                          onChange={(e) => setPostForm({
                            ...postForm,
                            custom_field_values: { ...postForm.custom_field_values, [field.name]: e.target.value }
                          })}
                          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreatePostModal(false);
                  setPostForm({
                    title: '',
                    slug: '',
                    content: '',
                    meta_title: '',
                    meta_description: '',
                    status: 'draft',
                    custom_field_values: {}
                  });
                }}
                className="px-4 py-2 text-sm border rounded-lg"
              >
                Cancel
              </button>
              <button onClick={handleCreatePost} className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                Create Post
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Custom Field Modal */}
      {showAddFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Custom Field</h3>
              <button onClick={() => setShowAddFieldModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Name *</label>
                <input
                  type="text"
                  value={newFieldForm.name}
                  onChange={(e) => setNewFieldForm({...newFieldForm, name: e.target.value})}
                  placeholder="e.g., Price, Location, Date"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field Type *</label>
                <select
                  value={newFieldForm.type}
                  onChange={(e) => setNewFieldForm({...newFieldForm, type: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="text">Text (Single Line)</option>
                  <option value="textarea">Text Area (Multi-line)</option>
                  <option value="number">Number</option>
                  <option value="email">Email</option>
                  <option value="url">URL</option>
                  <option value="date">Date</option>
                  <option value="datetime">Date & Time</option>
                  <option value="checkbox">Checkbox (Yes/No)</option>
                  <option value="select">Select Dropdown</option>
                  <option value="image">Image Upload</option>
                  <option value="file">File Upload</option>
                  <option value="wysiwyg">Rich Text Editor</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={newFieldForm.description}
                  onChange={(e) => setNewFieldForm({...newFieldForm, description: e.target.value})}
                  placeholder="Help text for this field..."
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddFieldModal(false);
                  setNewFieldForm({ name: '', type: 'text', description: '' });
                }}
                className="px-4 py-2 text-sm border rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!newFieldForm.name.trim()) {
                    alert('Please enter a field name');
                    return;
                  }
                  setCustomFields([...customFields, { ...newFieldForm }]);
                  setShowAddFieldModal(false);
                  setNewFieldForm({ name: '', type: 'text', description: '' });
                }}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Add Field
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}